// ============================================================
// Fee Type Service
// ============================================================

import { feeTypesApi } from '@/api/feeTypes.api';
import { generateId } from '@/utils/generateId';

// Local mock storage fallback when backend is unavailable or for demo testing
const initialMockFeeTypes = {
  '481d8157-6bdd-458a-ae11-153a0fc6d163': [
    {
      id: '435d0e8a-ee9a-4978-8dfc-d32bc9724b18',
      code: 'ADM001',
      name: 'Admission Fee',
      description: 'One-time admission fee collected during new student admission.',
      frequency: 'ONE_TIME',
      category: 'ACADEMIC',
      taxable: false,
      tax_percentage: 0,
      refundable: false,
      is_optional: false,
      is_active: true,
      school_id: '481d8157-6bdd-458a-ae11-153a0fc6d163',
      created_by: 'e311ca80-c78b-47e0-83a9-abf4978c94ba',
      updated_by: 'e311ca80-c78b-47e0-83a9-abf4978c94ba',
      created_at: '2026-07-26T16:34:16',
      updated_at: '2026-07-26T16:34:16',
    },
    {
      id: '3d260220-4821-4e96-b575-378a31a8fee2',
      code: 'TUI001',
      name: 'Monthly Tuition Fee',
      description: 'Monthly tuition fee for academic classes.',
      frequency: 'MONTHLY',
      category: 'ACADEMIC',
      taxable: false,
      tax_percentage: 0,
      refundable: false,
      is_optional: false,
      is_active: true,
      school_id: '481d8157-6bdd-458a-ae11-153a0fc6d163',
      created_by: 'e311ca80-c78b-47e0-83a9-abf4978c94ba',
      updated_by: 'e311ca80-c78b-47e0-83a9-abf4978c94ba',
      created_at: '2026-07-26T16:36:16',
      updated_at: '2026-07-26T16:36:16',
    },
    {
      id: '5e781190-7cc2-4889-a2e1-456bcd789012',
      code: 'BUS001',
      name: 'Transport Fee',
      description: 'Monthly school bus transportation fee.',
      frequency: 'MONTHLY',
      category: 'TRANSPORT',
      taxable: true,
      tax_percentage: 5,
      refundable: false,
      is_optional: true,
      is_active: true,
      school_id: '481d8157-6bdd-458a-ae11-153a0fc6d163',
      created_by: 'e311ca80-c78b-47e0-83a9-abf4978c94ba',
      updated_by: 'e311ca80-c78b-47e0-83a9-abf4978c94ba',
      created_at: '2026-07-26T16:38:00',
      updated_at: '2026-07-26T16:38:00',
    },
  ],
};

export const feeTypeService = {
  getFeeTypes: async (schoolId, skip = 0, limit = 100) => {
    if (!schoolId) throw new Error('School ID is required');
    try {
      const data = await feeTypesApi.getFeeTypes(schoolId, skip, limit);
      return Array.isArray(data) ? data : data.data || [];
    } catch (err) {
      console.warn('API error fetching fee types, using fallback cache:', err.message);
      return initialMockFeeTypes[schoolId] || [];
    }
  },

  getFeeTypeById: async (schoolId, id) => {
    if (!schoolId) throw new Error('School ID is required');
    try {
      return await feeTypesApi.getFeeTypeById(schoolId, id);
    } catch (err) {
      console.warn('API error fetching fee type details:', err.message);
      const list = initialMockFeeTypes[schoolId] || [];
      const item = list.find((ft) => ft.id === id);
      if (!item) throw err;
      return item;
    }
  },

  createFeeType: async (schoolId, payload) => {
    if (!schoolId) throw new Error('School ID is required');
    try {
      return await feeTypesApi.createFeeType(schoolId, payload);
    } catch (err) {
      if (err.response) {
        const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to create fee type';
        throw new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
      }
      console.warn('API error creating fee type, saving locally:', err.message);
      const newFeeType = {
        id: generateId('ft-'),
        ...payload,
        school_id: schoolId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      if (!initialMockFeeTypes[schoolId]) {
        initialMockFeeTypes[schoolId] = [];
      }
      initialMockFeeTypes[schoolId].unshift(newFeeType);
      return { success: true, message: 'Fee type created', data: newFeeType };
    }
  },

  updateFeeType: async (schoolId, id, payload) => {
    if (!schoolId) throw new Error('School ID is required');
    try {
      return await feeTypesApi.updateFeeType(schoolId, id, payload);
    } catch (err) {
      if (err.response) {
        const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to update fee type';
        throw new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
      }
      console.warn('API error updating fee type, updating locally:', err.message);
      const list = initialMockFeeTypes[schoolId] || [];
      const index = list.findIndex((ft) => ft.id === id);
      if (index !== -1) {
        list[index] = {
          ...list[index],
          ...payload,
          updated_at: new Date().toISOString(),
        };
        return { success: true, message: 'Fee type updated', data: list[index] };
      }
      throw err;
    }
  },

  deleteFeeType: async (schoolId, id) => {
    if (!schoolId) throw new Error('School ID is required');
    try {
      return await feeTypesApi.deleteFeeType(schoolId, id);
    } catch (err) {
      if (err.response) {
        const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to delete fee type';
        throw new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
      }
      console.warn('API error deleting fee type, deleting locally:', err.message);
      if (initialMockFeeTypes[schoolId]) {
        initialMockFeeTypes[schoolId] = initialMockFeeTypes[schoolId].filter((ft) => ft.id !== id);
      }
      return { message: 'Fee type deleted successfully.' };
    }
  },
};
